/* eslint-disable no-console */
import { useCallback } from 'react';
import useUser from '@ubeya/shared/hooks/auth/useUser';

const toMinimalState = (state) => state.map(({ colId, hide }) => ({ colId, hide }));

const mergeColumnOrder = (savedColIds, currentColIds) => {
  const savedSet = new Set(savedColIds);
  const slots = currentColIds.map((id, idx) => (savedSet.has(id) ? idx : -1)).filter((idx) => idx >= 0);
  const knownIds = savedColIds.filter((id) => currentColIds.includes(id));

  const merged = [...currentColIds];
  knownIds.forEach((colId, i) => {
    merged[slots[i]] = colId;
  });
  return merged;
};

export const usePersistedColumnState = ({ storageKey }) => {
  const { data: userData } = useUser();
  const adminId = userData?.id;
  const scopedKey = adminId ? `${storageKey}-admin-${adminId}` : storageKey;

  const loadColumnState = useCallback(
    (api) => {
      if (!api) return false;

      const raw = localStorage.getItem(scopedKey);
      if (!raw) return false;

      try {
        const savedState = JSON.parse(raw);

        if (Array.isArray(savedState)) {
          const state = toMinimalState(savedState);
          if (state.length === 0) return false;

          api.applyColumnState({ state, applyOrder: false });

          const savedColIds = state.map(({ colId }) => colId);
          const currentColIds = api.getColumnState().map(({ colId }) => colId);
          const orderedIds = mergeColumnOrder(savedColIds, currentColIds);

          api.applyColumnState({
            state: orderedIds.map((colId) => ({ colId })),
            applyOrder: true
          });

          return true;
        }

        // Legacy format: { colId: hide } object
        const visibilityState = Object.keys(savedState).map((colId) => ({ colId, hide: savedState[colId] }));
        if (visibilityState.length > 0) {
          api.applyColumnState({ state: visibilityState });
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    [scopedKey]
  );

  const saveColumnState = useCallback(
    (api) => {
      if (!api) return;
      try {
        const state = api.getColumnState();
        if (!state) return;
        localStorage.setItem(scopedKey, JSON.stringify(toMinimalState(state)));
      } catch (error) {
        console.warn('TableAgGrid: Error saving column state', error);
      }
    },
    [scopedKey]
  );

  return { loadColumnState, saveColumnState };
};
